// THE BOARD - Orders Summary > Today's Goal.
//
// Greg, 2026-09-19: "I'm not opposed to instead of burying all of this information in an email,
// just having a summary and a link to click to take you to it... that way you probably have more
// flexibility in the tools to build the visuals and it takes them already into the site and gets
// them motivated rather than having one more email to try to scroll through and read."
//
// So the weekday email is a DOORBELL and this is where the work happens - the same split the
// morning frog brief already uses. Everything here is also in the email as a two-line summary.
//
// ⚠️ ORDER IS THE DESIGN, AND HE CHANGED IT DELIBERATELY: the goal, then what needs a person
// TODAY, then how yesterday went. "They need to get a full picture of what needs today before
// they move on to looking at how we did yesterday."
//
// ⚠️ IT DERIVES NOTHING. Every figure comes from lib/outreach-huddle.js, so this board, the email
// and the win-back tab cannot tell three different stories about the same day.
import React, { useState, useEffect, useCallback } from 'react';
import { Loader, RefreshCw, AlertTriangle, Target, Mail, Clock } from 'lucide-react';
import { getAccessToken } from '../services/auth';

const NAVY = 'var(--navy)';
const ORANGE = 'var(--orange)';

async function callHuddle(action, params = {}) {
  const base = window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions' : '/.netlify/functions';
  const token = getAccessToken();
  const res = await fetch(`${base}/outreach-huddle`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({ action, ...params }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || body.error) throw new Error(body.error || `HTTP ${res.status}`);
  return body;
}

const firstName = (n) => String(n || '').trim().split(/\s+/)[0] || n;
const usDate = (iso) => {
  const p = String(iso || '').split('-');
  return p.length === 3 ? `${+p[1]}/${+p[2]}` : String(iso || '');
};
const monthDay = (iso) => {
  if (!iso) return '';
  const d = new Date(iso + 'T12:00:00Z');
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', timeZone: 'UTC' });
};
const rate = (r) => (r == null ? '–' : r + '%');

const card = {
  background: '#fff', border: '1px solid var(--gray-200, #e6ebf0)', borderRadius: 10,
  padding: '16px 18px', marginBottom: 14,
};
const label = {
  fontSize: 11, fontWeight: 700, letterSpacing: '.6px', textTransform: 'uppercase',
  color: 'var(--gray-500)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6,
};

// ── The thermometer ──────────────────────────────────────────────────────────
// ⚠️ IT DOES NOT START AT ZERO, AND IT SAYS SO. 6,150 of 7,000 on a zero-based bar is 88% full and
// reads as "nearly done" - which is true and useless, because the 850 that matter are a sliver.
// The bar is scaled from 5,900 so a day's movement is actually visible, and the axis is labelled
// with its real floor rather than implying the scale starts at nothing.
function Thermometer({ goal }) {
  const FLOOR = 5900;
  const span = goal.goal - FLOOR;
  const pos = Math.max(0, Math.min(100, (100 * (goal.current - FLOOR)) / span));
  const yday = goal.yesterdayNet;
  const ydayWidth = yday > 0 ? Math.max(0.6, (100 * yday) / span) : 0;

  return (
    <div style={{ ...card, background: 'linear-gradient(180deg,#ffffff 0%,#f7fafc 100%)' }}>
      <div style={label}><Target size={13} /> The goal &mdash; renewing VIP members</div>

      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 38, fontWeight: 800, color: NAVY, lineHeight: 1 }}>
          {goal.current == null ? '–' : goal.current.toLocaleString('en-US')}
        </div>
        <div style={{ fontSize: 15, color: 'var(--gray-600)' }}>
          of {goal.goal.toLocaleString('en-US')}
        </div>
        <div style={{ flex: 1 }} />
        <div style={{ fontSize: 22, fontWeight: 800, color: ORANGE }}>
          {goal.gap == null ? '–' : goal.gap.toLocaleString('en-US')}
        </div>
        <div style={{ fontSize: 13, color: 'var(--gray-600)' }}>to go</div>
      </div>

      <div style={{ position: 'relative', marginTop: 14, marginBottom: 6 }}>
        <div style={{ height: 22, borderRadius: 11, background: '#e7edf3', overflow: 'hidden', display: 'flex' }}>
          <div style={{ width: `${pos - ydayWidth}%`, background: NAVY }} />
          {/* Yesterday's movement, highlighted on the end of the bar so a good day is visible. */}
          {ydayWidth > 0 && <div style={{ width: `${ydayWidth}%`, background: ORANGE }} title={`+${yday} yesterday`} />}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--gray-500)', marginTop: 4 }}>
          <span>{FLOOR.toLocaleString('en-US')}</span>
          <span style={{ color: ORANGE, fontWeight: 700 }}>
            {yday > 0 ? `+${yday} on the pool yesterday` : yday === 0 ? 'pool flat yesterday' : `${yday} on the pool yesterday`}
          </span>
          <span style={{ fontWeight: 700, color: NAVY }}>{goal.goal.toLocaleString('en-US')} &middot; bonus</span>
        </div>
      </div>

      {/* ⚠️ SIGNUPS AND THE POOL SIT SIDE BY SIDE AND ARE NEVER SUBTRACTED. The first cut printed
          "16 joined - 4 lapsed = +12" and Greg pushed back: "technically none of them turned off
          yesterday... we didn't actually lose anybody." He was right. Nothing recorded a single
          opt-out, and on 17 Sept the pool moved +30 on 10 signups - three times the signups, which
          no departure story explains. The pool is a 7:30am snapshot of a mirror that lags, so the
          difference is timing, not loss. Inventing a departure to balance it would put a false
          accusation on a board the team reads every morning. */}
      {goal.yesterdaySignups != null && (
        <div style={{
          display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center',
          marginTop: 12, padding: '10px 12px', borderRadius: 8, background: '#f4f8fb',
        }}>
          <div>
            <div style={{ fontSize: 22, fontWeight: 800, color: NAVY, lineHeight: 1 }}>
              {goal.yesterdaySignups}
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--gray-600)' }}>signed up yesterday</div>
          </div>
          <div style={{ width: 1, alignSelf: 'stretch', background: 'var(--gray-200, #e6ebf0)' }} />
          <div>
            <div style={{ fontSize: 22, fontWeight: 800, color: ORANGE, lineHeight: 1 }}>
              {goal.yesterdayNet > 0 ? '+' : ''}{goal.yesterdayNet}
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--gray-600)' }}>movement on the pool</div>
          </div>
          <div style={{ flex: '1 1 260px', fontSize: 11.5, color: 'var(--gray-500)', lineHeight: 1.5 }}>
            These two will not tie on any given day and that is expected, not an error. Signups are
            counted the moment a membership moves; the pool is a snapshot taken at{' '}
            {goal.snapshotHour || '7:30am'} of a mirror that lags behind production, so arrivals land
            in whichever day the sync caught them. <b>The difference is timing, not members lost.</b>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 12 }}>
        <Pace
          title="At the last 7 days' pace"
          per={goal.measuredPace}
          when={goal.dateAtMeasured}
          days={goal.daysAtMeasured}
          tone="grey"
        />
        <Pace
          title={`With all ${(goal.fullTeam || 150)} going out, Mon to Fri`}
          per={goal.modelledPace}
          when={goal.dateAtModelled}
          days={goal.daysAtModelled}
          tone="navy"
        />
      </div>

      {goal.convRate != null && goal.modelledPace != null && (
        <p style={{ margin: '10px 0 0', fontSize: 11.5, color: 'var(--gray-500)', lineHeight: 1.5 }}>
          The second date is what happens if the team sends its full {goal.fullTeam} on every
          working day: {goal.fullTeam} &times; {rate(goal.convRate)} coming back
          ({goal.convSample.back} of {goal.convSample.sent} so far), plus the {goal.basePace}/day
          the website and Delivered by day bring in on their own.
          {goal.workingDaysToGoal != null && (
            <> That is <b>{goal.workingDaysToGoal} working days</b>
              {goal.sendsToGoal != null && <> and about {goal.sendsToGoal.toLocaleString()} invites</>}.
            </>
          )}
          {' '}Weekends earn the website&rsquo;s share and nothing from sending, and the base is
          adjusted for the season &mdash; October runs a little above September, December well
          above it, November is the soft one.
          {/* ⚠️ Shown whenever the old arithmetic disagreed, so the date moving reads as a
              correction with a reason rather than the board changing its mind. */}
          {goal.naiveDateAtModelled && goal.naiveDateAtModelled !== goal.dateAtModelled && (
            <> Counting weekends as sending days would have said {monthDay(goal.naiveDateAtModelled)}.</>
          )}
        </p>
      )}
    </div>
  );
}

function Pace({ title, per, when, days, tone }) {
  const navy = tone === 'navy';
  return (
    <div style={{
      flex: '1 1 200px', minWidth: 190, borderRadius: 8, padding: '10px 12px',
      border: `1px solid ${navy ? NAVY : 'var(--gray-200, #e6ebf0)'}`,
      background: navy ? 'rgba(0,57,92,0.04)' : '#fff',
    }}>
      <div style={{ fontSize: 11.5, color: 'var(--gray-600)' }}>{title}</div>
      <div style={{ fontSize: 17, fontWeight: 800, color: navy ? NAVY : 'var(--gray-700)', marginTop: 2 }}>
        {per == null ? 'not enough history' : `+${per} a day`}
      </div>
      <div style={{ fontSize: 12.5, color: navy ? NAVY : 'var(--gray-600)', marginTop: 1 }}>
        {when ? `${monthDay(when)}` : '–'}
        {days ? <span style={{ color: 'var(--gray-500)' }}> &middot; {days} days</span> : null}
      </div>
    </div>
  );
}

// ── Today ────────────────────────────────────────────────────────────────────
function TodayCard({ d }) {
  const del = d.delivered;
  const urgent = del.remaining > 0 && d.daysLeft <= 2;
  const items = [
    {
      // What is LEFT, not the flat target - the card used to read 150 all day, including after
      // 50 had gone. A number that never moves is one people stop believing.
      big: d.winbackLeftToday != null ? d.winbackLeftToday : d.winbackTarget,
      head: (d.winbackLeftToday === 0) ? "win-backs left today — all sent" : 'win-backs still to send today',
      sub: d.winbackSentToday
        ? `${d.winbackSentToday} of ${d.winbackTarget} already out. ${d.cap} each for ${d.reps.map(firstName).join(', ') || 'the team'}.`
        : `${d.cap} each for ${d.reps.map(firstName).join(', ') || 'the team'}. Get them out early — almost every reply lands the same afternoon.`,
      tone: d.winbackLeftToday === 0 ? 'green' : 'navy',
    },
  ];
  if (del.total > 0) {
    items.push({
      big: del.remaining,
      head: 'Delivered-by-day invites left',
      sub: `From ${usDate(del.start)}–${usDate(del.end)}. ${d.daysLeft > 0
        ? `${d.daysLeft} working ${d.daysLeft === 1 ? 'day' : 'days'} left this week.`
        : 'Due now.'}`,
      tone: urgent ? 'orange' : 'navy',
    });
  }
  if (d.human.waiting.length > 0) {
    items.push({
      big: d.human.waiting.length,
      head: 'said yes or promised to join, not signed up',
      sub: `Longest has been waiting ${d.human.waiting[0].days >= 1
        ? `${d.human.waiting[0].days} ${d.human.waiting[0].days === 1 ? 'day' : 'days'}`
        : `${d.human.waiting[0].hours} hours`}. They stay on this list until they join or their rep presses Not joining.`,
      tone: 'orange',
    });
  }
  return (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
      {items.map((it, i) => (
        <div key={i} style={{
          ...card, marginBottom: 0, flex: '1 1 240px',
          borderColor: it.tone === 'orange' ? ORANGE : 'var(--gray-200, #e6ebf0)',
          background: it.tone === 'orange' ? '#fff8f3' : '#fff',
        }}>
          <div style={{ fontSize: 34, fontWeight: 800, lineHeight: 1, color: it.tone === 'orange' ? ORANGE : NAVY }}>{it.big}</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: NAVY, marginTop: 3 }}>{it.head}</div>
          <div style={{ fontSize: 12, color: 'var(--gray-600)', marginTop: 5, lineHeight: 1.45 }}>{it.sub}</div>
        </div>
      ))}
    </div>
  );
}

// ── Who is waiting ───────────────────────────────────────────────────────────
// ⚠️ A YES CAN END TWO WAYS, AND ONLY ONE OF THEM WAS ON THE BOARD. It closes by itself when the
// customer becomes a member. The other honest ending is "we tried and could not finish" - card
// declined, emailed, called, no answer - and until 2026-10-07 that had nowhere to go, so the row
// sat here for days reading as work nobody was doing. Rachael: "they are sitting in the Said yes,
// not signed up without a way to clear it."
//
// ⚠️ IT ASKS WHY, AND CANCELLING CLOSES NOTHING. The reason is what tells the next person this was
// a considered call rather than a tick to make the number go away. It is the same tick the
// Replies board reads, and a new reply from the customer puts them straight back on the list.
//
// ⚠️ IT CLOSES THE CUSTOMER, NOT ONE EMAIL, SINCE 2026-10-29. The list became "promised to join"
// (lib/outreach-promised.js), which reads every reply they sent, so ticking one email read could
// never take them off - and must not, or "I'll buy it on my next order" disappears the day it lands.
async function closeWaiting(r) {
  const note = window.prompt(
    `Take ${r.name} off the promised-to-join list?\n\nUse this when they are not joining, or you have tried and could not get them signed up. `
    + 'They come back on if they write to us again.\n\nWhat happened?',
    'Card declined. Emailed and called, no response.');
  if (note === null || !note.trim()) return false;
  const base = window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions' : '/.netlify/functions';
  const token = getAccessToken();
  const res = await fetch(`${base}/outreach-replies-board`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({ action: 'not-joining', customerId: r.customerId, note: note.trim() }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || body.error || body.allowed === false) {
    window.alert('Could not close that one: ' + (body.error || 'not allowed'));
    return false;
  }
  return true;
}

function WaitingCard({ human, onChanged }) {
  if (!human.waiting.length && !human.lost.length) return null;
  return (
    <div style={card}>
      <div style={label}><Clock size={13} /> Needs a person today</div>
      {human.waiting.length > 0 && (
        <table className="product-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', fontSize: 11, padding: '6px 8px' }}>Said yes or promised to join, not signed up</th>
              <th style={{ textAlign: 'left', fontSize: 11, padding: '6px 8px' }}>Their rep</th>
              <th style={{ textAlign: 'right', fontSize: 11, padding: '6px 8px' }}>Waiting</th>
            </tr>
          </thead>
          <tbody>
            {human.waiting.map((r) => (
              <tr key={r.customerId}>
                <td style={{ padding: '6px 8px', fontSize: 13 }}>
                  <b>{r.name}</b><br />
                  <span style={{ color: 'var(--gray-500)', fontSize: 11.5 }}>{r.email}</span>
                  {r.said && (
                    <div style={{ fontSize: 12, color: 'var(--gray-700)', marginTop: 2 }}>
                      &ldquo;{r.said.length > 140 ? r.said.slice(0, 140) + '…' : r.said}&rdquo;
                    </div>
                  )}
                </td>
                <td style={{ padding: '6px 8px', fontSize: 12.5, color: 'var(--gray-600)' }}>{firstName(r.rep)}</td>
                <td style={{
                  padding: '6px 8px', fontSize: 12.5, textAlign: 'right', whiteSpace: 'nowrap',
                  color: r.days >= 2 ? ORANGE : 'var(--gray-600)', fontWeight: r.days >= 2 ? 700 : 400,
                }}>
                  {r.days >= 1 ? `${r.days} ${r.days === 1 ? 'day' : 'days'}` : `${r.hours}h`}
                  {r.customerId && (
                    <div>
                      <button
                        onClick={async () => { if (await closeWaiting(r)) onChanged && onChanged(); }}
                        style={{ border: 'none', background: 'none', color: 'var(--gray-500)', fontSize: 11, fontWeight: 400, textDecoration: 'underline', cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}
                      >Not joining</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {human.otherReplies > 0 && (
        <p style={{ margin: '8px 0 0', fontSize: 11.5, color: 'var(--gray-500)' }}>
          {human.otherReplies} other {human.otherReplies === 1 ? 'customer has' : 'customers have'} written
          back with a question and {human.otherReplies === 1 ? 'is' : 'are'} not a member yet. They are not
          on this list because they did not say yes, but somebody should read them.
        </p>
      )}
      {human.lost.length > 0 && (
        <div style={{ marginTop: 12, background: '#fff8f3', border: `1px solid ${ORANGE}`, borderRadius: 8, padding: '10px 12px' }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: ORANGE, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 5 }}>
            <AlertTriangle size={13} /> Sends we cannot account for, last 7 days
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5 }}>
            {human.lost.map((r, i) => (
              <li key={i} style={{ marginBottom: 2 }}>
                <b>{firstName(r.rep)}</b> &ndash; {r.n} {(r.mode === 'winback' ? 'win-back' : r.mode === 'winback_followup' ? 'follow-up' : 'invite')}{r.n === 1 ? '' : 's'} not in Sent Items
              </li>
            ))}
          </ul>
          <p style={{ margin: '6px 0 0', fontSize: 11.5, color: 'var(--gray-600)' }}>
            Clicked in CPx but not in Sent Items. Usually the new Outlook, which takes the message
            into a hidden Outbox and never sends it. Check Sent Items, and resend if it is not there.
          </p>
        </div>
      )}
    </div>
  );
}

// ── Yesterday ────────────────────────────────────────────────────────────────
function YesterdayCard({ y }) {
  if (!y.total.sent) {
    return (
      <div style={card}>
        <div style={label}><Mail size={13} /> How yesterday went</div>
        <div style={{ fontSize: 13, color: 'var(--gray-500)' }}>No win-backs went out.</div>
      </div>
    );
  }
  const cell = { padding: '6px 8px', fontSize: 13, textAlign: 'right' };
  return (
    <div style={card}>
      <div style={label}><Mail size={13} /> How yesterday went</div>
      <table className="product-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left', fontSize: 11, padding: '6px 8px' }}>Rep</th>
            <th style={{ ...cell, fontSize: 11 }}>Confirmed sent</th>
            <th style={{ ...cell, fontSize: 11 }}>Opened</th>
            <th style={{ ...cell, fontSize: 11 }}>Replied</th>
            <th style={{ ...cell, fontSize: 11 }}>Joined</th>
          </tr>
        </thead>
        <tbody>
          {y.reps.map((r) => {
            const checking = r.sent - r.confirmed - r.missing;
            return (
              <tr key={r.rep}>
                <td style={{ padding: '6px 8px', fontSize: 13 }}><b>{firstName(r.rep)}</b></td>
                <td style={cell}>
                  <b style={{ color: r.missing > 0 ? ORANGE : NAVY }}>{r.confirmed}</b>
                  {r.missing > 0 && <span style={{ color: ORANGE, fontSize: 11.5 }}> ({r.missing} not found)</span>}
                  {r.missing === 0 && checking > 0 && <span style={{ color: 'var(--gray-500)', fontSize: 11.5 }}> ({checking} checking)</span>}
                </td>
                <td style={cell}>{r.opened}<span style={{ color: 'var(--gray-500)', fontSize: 11 }}> / {r.tracked} &middot; {rate(r.openRate)}</span></td>
                <td style={cell}>{r.replied}<span style={{ color: 'var(--gray-500)', fontSize: 11 }}> &middot; {rate(r.replyRate)}</span></td>
                <td style={cell}><b style={{ color: r.joined > 0 ? NAVY : 'var(--gray-400)' }}>{r.joined}</b></td>
              </tr>
            );
          })}
          <tr style={{ borderTop: `2px solid ${NAVY}` }}>
            <td style={{ padding: '6px 8px', fontSize: 13, fontWeight: 700 }}>Team</td>
            <td style={{ ...cell, fontWeight: 700 }}>{y.total.confirmed}</td>
            <td style={{ ...cell, fontWeight: 700 }}>{rate(y.total.openRate)}</td>
            <td style={{ ...cell, fontWeight: 700 }}>{rate(y.total.replyRate)}</td>
            <td style={{ ...cell, fontWeight: 700 }}>{y.total.joined}</td>
          </tr>
        </tbody>
      </table>
      <p style={{ margin: '6px 0 0', fontSize: 11.5, color: 'var(--gray-500)' }}>
        Confirmed sent is what we can find in Sent Items, not what was clicked. Opened is out of the
        sends carrying a pixel and undercounts badly, because most mail clients block images — it is
        a floor, not a score.
      </p>
    </div>
  );
}

// ── The April 2026 group ─────────────────────────────────────────────────────
function CohortCard({ cohort, daysToFinish }) {
  const p = cohort.members > 0 ? Math.round((100 * cohort.emailed) / cohort.members) : 0;
  return (
    <div style={card}>
      <div style={label}>The April 2026 group &mdash; our warmest names</div>
      <div style={{ fontSize: 14, color: NAVY, fontWeight: 700 }}>
        {cohort.emailed} of {cohort.members.toLocaleString('en-US')} emailed
        <span style={{ fontWeight: 400, color: 'var(--gray-500)', fontSize: 12.5 }}> &middot; {p}%</span>
      </div>
      <div style={{ height: 10, borderRadius: 5, background: '#e7edf3', overflow: 'hidden', margin: '8px 0 6px' }}>
        <div style={{ width: `${p}%`, height: '100%', background: NAVY }} />
      </div>
      <div style={{ fontSize: 12.5, color: 'var(--gray-600)' }}>
        <b style={{ color: NAVY, fontSize: 15 }}>{cohort.remaining.toLocaleString('en-US')}</b> left to work
        {daysToFinish ? ` · about ${daysToFinish} working ${daysToFinish === 1 ? 'day' : 'days'} at yesterday's pace` : ''}
      </div>
      <p style={{ margin: '8px 0 0', fontSize: 11.5, color: 'var(--gray-500)', lineHeight: 1.5 }}>
        These are the warmest names we have &mdash; their card failed, they did not choose to leave.
        {' '}{cohort.olderRemaining.toLocaleString('en-US')} more are waiting in the 2017 to 2025 groups
        and they will be colder, so it is worth finishing this one first.
      </p>
    </div>
  );
}

export default function VipGoalBoard() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(true);

  const load = useCallback(async () => {
    setBusy(true);
    try { const r = await callHuddle('dashboard'); setD(r.data); setErr(null); }
    catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // ⚠️ REFRESH WHEN HE COMES BACK TO THE TAB. Greg, 2026-09-22: "can we make this page refresh
  // each time it loads?" - he had the board open beside the wall board and they disagreed,
  // because a tab left open all morning keeps whatever it fetched when it mounted. The numbers
  // on here move all day (signups, sends, replies), so a stale board is a board that gets
  // argued with.
  //
  // ⚠️ Throttled to once a minute. This is a ~6s server call and alt-tabbing is not a request
  // for fresh data; without the guard, flicking between windows would queue a call each time.
  useEffect(() => {
    let last = Date.now();
    const maybe = () => {
      if (document.visibilityState && document.visibilityState !== 'visible') return;
      if (Date.now() - last < 60000) return;
      last = Date.now();
      load();
    };
    window.addEventListener('focus', maybe);
    document.addEventListener('visibilitychange', maybe);
    return () => {
      window.removeEventListener('focus', maybe);
      document.removeEventListener('visibilitychange', maybe);
    };
  }, [load]);

  if (busy && !d) {
    return <div style={{ padding: 40, textAlign: 'center', color: 'var(--gray-500)' }}>
      <Loader size={18} className="spin" /> Loading the board&hellip;
    </div>;
  }
  if (err) {
    return <div style={{ ...card, borderColor: ORANGE, background: '#fff5f5', color: '#a12020', fontSize: 13 }}>{err}</div>;
  }
  if (!d) return null;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <div style={{ flex: 1, fontSize: 12.5, color: 'var(--gray-600)' }}>
          Everything on this board is for <b>today</b>. Yesterday is underneath it.
        </div>
        <button onClick={load} className="btn btn-ghost" style={{ fontSize: 12 }}>
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {d.goal && <Thermometer goal={d.goal} />}
      <TodayCard d={d} />
      {/* ⚠️ Waiting sits ABOVE yesterday, by Greg's instruction on 2026-09-19 - the full picture of
          what needs doing today comes before any look back. */}
      <WaitingCard human={d.human} onChanged={load} />
      <YesterdayCard y={d.yesterday} />
      <CohortCard cohort={d.cohort} daysToFinish={d.daysToFinish2026} />
    </div>
  );
}
